import { Alert, Chip } from "@mui/material";
import * as React from "react";

import { DeviceSelector } from "@/shared/components/devices";
import { useAllDevicesByOrg } from "@/shared/domain/devices/hooks/useAllDevicesByOrg";
import { Button } from "@/shared/foundation/primitives";

import styles from "./CreateSessionPage.module.css";

interface DevicesSectionProps {
  orgId: string;
  inferredDeviceIds: string[];
  isInferringDevices: boolean;
  inferDevicesError: Error | null;
  explicitDeviceIds: string[];
  onAddDevice: (deviceId: string) => void;
  onRemoveDevice: (deviceId: string) => void;
}

/**
 * Devices inferred from the selected files are display-only — the backend
 * links them to the session automatically. Devices the user adds here are
 * sent explicitly in the create request.
 */
export function DevicesSection({
  orgId,
  inferredDeviceIds,
  isInferringDevices,
  inferDevicesError,
  explicitDeviceIds,
  onAddDevice,
  onRemoveDevice,
}: DevicesSectionProps) {
  const [pendingDeviceId, setPendingDeviceId] = React.useState<string | null>(
    null,
  );

  // The same query DeviceSelector uses for its options (shared cache entry).
  // Add is gated on membership so free-typed text that names no existing
  // device can't reach the create request, where it would fail the
  // device-attachment FK and abort the whole session create at submit time.
  const { data: devices } = useAllDevicesByOrg(orgId);
  const pendingDeviceExists =
    pendingDeviceId !== null &&
    (devices ?? []).some((device) => device.device_id === pendingDeviceId);

  const handleAdd = () => {
    if (pendingDeviceId === null || !pendingDeviceExists) {
      return;
    }
    onAddDevice(pendingDeviceId);
    setPendingDeviceId(null);
  };

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>Devices</h2>
      <InferredDevices
        deviceIds={inferredDeviceIds}
        isLoading={isInferringDevices}
        error={inferDevicesError}
      />
      <div className={styles.field}>
        {/* `device-selector` is the id DeviceSelector assigns its input. */}
        <label htmlFor="device-selector" className={styles.label}>
          Attach devices
        </label>
        <div className={styles.addDeviceRow}>
          <div className={styles.deviceSelectorWrap}>
            <DeviceSelector
              deviceId={pendingDeviceId}
              setDeviceId={setPendingDeviceId}
              orgId={orgId}
              placeholder="Select a device to explicitly attach"
            />
          </div>
          <Button
            variant="outlined"
            disabled={!pendingDeviceExists}
            onClick={handleAdd}
          >
            Add
          </Button>
        </div>
        <ExplicitDeviceChips
          deviceIds={explicitDeviceIds}
          onRemove={onRemoveDevice}
        />
      </div>
    </section>
  );
}

function InferredDevices({
  deviceIds,
  isLoading,
  error,
}: {
  deviceIds: string[];
  isLoading: boolean;
  error: Error | null;
}) {
  if (error !== null) {
    return (
      <Alert severity="error">
        Failed to infer devices from the selected files: {error.message}
      </Alert>
    );
  }
  // While a first inference is in flight there are no held device ids yet;
  // say so rather than rendering nothing. A refetch keeps showing the held
  // previous result below instead.
  if (isLoading && deviceIds.length === 0) {
    return (
      <div className={styles.field}>
        <span className={styles.label}>Inferred from selected files</span>
        <p className={styles.helpText}>
          Inferring devices from the selected files…
        </p>
      </div>
    );
  }
  if (deviceIds.length === 0) {
    return null;
  }
  return (
    <div className={styles.field}>
      <span className={styles.label}>Inferred from selected files</span>
      <ul className={styles.chipList} aria-label="Inferred devices">
        {deviceIds.map((deviceId) => (
          <li key={deviceId}>
            <Chip className={styles.deviceChip} label={deviceId} size="small" />
          </li>
        ))}
      </ul>
      <p className={styles.helpText}>
        These devices are associated with the selected files and will be linked
        to the session automatically.
      </p>
    </div>
  );
}

function ExplicitDeviceChips({
  deviceIds,
  onRemove,
}: {
  deviceIds: string[];
  onRemove: (deviceId: string) => void;
}) {
  if (deviceIds.length === 0) {
    return null;
  }
  return (
    <ul className={styles.chipList} aria-label="Attached devices">
      {deviceIds.map((deviceId) => (
        <li key={deviceId}>
          <Chip
            className={styles.deviceChip}
            label={deviceId}
            size="small"
            onDelete={() => onRemove(deviceId)}
          />
        </li>
      ))}
    </ul>
  );
}
